import React, { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useClientes } from '../../context/ClientesContext';
import { useTaller3D } from '../../context/Taller3DContext';
import { formatearImporte } from '../../utils/calculo3d';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  ArrowLeft, Edit2, Building2, User as UserIcon, Mail, Phone, MapPin,
  FileText, Plus, StickyNote,
} from 'lucide-react';
import { ModalCliente } from '../../components/clientes/ModalCliente';

const CLASE_ESTADO: Record<string, string> = {
  borrador:  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  enviado:   'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  aceptado:  'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  rechazado: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

export const FichaCliente: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { clientePorId, loadingClientes } = useClientes();
  const { presupuestos3D } = useTaller3D();
  const [editando, setEditando] = useState(false);

  const cliente = clientePorId(id);

  const suyos = useMemo(
    () => presupuestos3D.filter(p => p.clienteId === id),
    [presupuestos3D, id],
  );

  const totales = useMemo(() => {
    const aceptado = suyos.filter(p => p.estado === 'aceptado')
      .reduce((s, p) => s + (p.resumen?.precioTotalConIva ?? 0), 0);
    const pendiente = suyos.filter(p => p.estado === 'enviado')
      .reduce((s, p) => s + (p.resumen?.precioTotalConIva ?? 0), 0);
    return { aceptado, pendiente };
  }, [suyos]);

  if (loadingClientes) {
    return <div className="max-w-4xl mx-auto px-4 py-10 text-gray-500 dark:text-gray-400">Cargando…</div>;
  }

  if (!cliente) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10">
        <p className="text-gray-600 dark:text-gray-400">Este cliente ya no existe.</p>
        <Link to="/clientes" className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-purple-600">
          <ArrowLeft className="w-4 h-4" /> Volver a Clientes
        </Link>
      </div>
    );
  }

  const direccion = [
    cliente.direccion,
    [cliente.codigoPostal, cliente.poblacion].filter(Boolean).join(' '),
    cliente.provincia,
    cliente.pais,
  ].filter(Boolean);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">

      <Link to="/clientes"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 dark:text-gray-400
                   hover:text-gray-900 dark:hover:text-white transition mb-4">
        <ArrowLeft className="w-4 h-4" /> Clientes
      </Link>

      <header className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 shrink-0 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
            {cliente.tipo === 'empresa'
              ? <Building2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              : <UserIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {cliente.nombre}
              {!cliente.activo && (
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide align-middle
                                 text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                  Inactivo
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {cliente.tipo === 'empresa' ? 'Empresa' : 'Particular'}
              {cliente.nif && ` · ${cliente.nif}`}
            </p>
          </div>
          <button onClick={() => setEditando(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg
                       border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300
                       hover:bg-gray-50 dark:hover:bg-gray-700 transition shrink-0">
            <Edit2 className="w-4 h-4" /> Editar
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          {cliente.email && (
            <a href={`mailto:${cliente.email}`}
              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-purple-600 transition">
              <Mail className="w-4 h-4 text-gray-400 shrink-0" /> {cliente.email}
            </a>
          )}
          {cliente.telefono && (
            <a href={`tel:${cliente.telefono}`}
              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-purple-600 transition">
              <Phone className="w-4 h-4 text-gray-400 shrink-0" /> {cliente.telefono}
            </a>
          )}
          {direccion.length > 0 && (
            <p className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 sm:col-span-2">
              <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
              <span>{direccion.join(', ')}</span>
            </p>
          )}
          {cliente.notas && (
            <p className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400 sm:col-span-2">
              <StickyNote className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
              <span className="whitespace-pre-wrap">{cliente.notas}</span>
            </p>
          )}
        </div>
      </header>

      {(totales.aceptado > 0 || totales.pendiente > 0) && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">Aceptado</p>
            <p className="font-mono text-lg tabular-nums text-green-600 dark:text-green-400 mt-0.5">
              {formatearImporte(totales.aceptado, '€')}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">Enviado, sin respuesta</p>
            <p className="font-mono text-lg tabular-nums text-blue-600 dark:text-blue-400 mt-0.5">
              {formatearImporte(totales.pendiente, '€')}
            </p>
          </div>
        </div>
      )}

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-bold text-gray-900 dark:text-white">
            Presupuestos 3D
            <span className="ml-2 font-normal text-sm text-gray-500 dark:text-gray-400">{suyos.length}</span>
          </h2>
          <button onClick={() => navigate(`/taller3d/calculadora?cliente=${cliente.id}`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg
                       bg-orange-600 hover:bg-orange-700 text-white transition">
            <Plus className="w-3.5 h-3.5" /> Nuevo para este cliente
          </button>
        </div>

        {suyos.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
            <FileText className="w-4 h-4" /> Todavía no le has hecho ningún presupuesto.
          </p>
        ) : (
          <div className="space-y-2">
            {suyos.map(p => (
              <Link key={p.id} to={`/taller3d/calculadora?id=${p.id}`}
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700
                           hover:border-gray-300 dark:hover:border-gray-600 transition">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{p.nombre}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {p.resumen?.unidades ?? 0} ud{(p.resumen?.unidades ?? 0) === 1 ? '' : 's'} ·{' '}
                    {format(p.updatedAt, "d MMM yyyy", { locale: es })}
                  </p>
                </div>
                <span className={`text-[11px] font-semibold rounded-full px-2.5 py-1 shrink-0
                                  ${CLASE_ESTADO[p.estado] ?? CLASE_ESTADO.borrador}`}>
                  {p.estado}
                </span>
                <span className="font-mono text-sm tabular-nums text-gray-900 dark:text-white shrink-0">
                  {formatearImporte(p.resumen?.precioTotalConIva ?? 0, p.parametros?.moneda ?? '€')}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {editando && (
        <ModalCliente cliente={cliente} onClose={() => setEditando(false)} />
      )}
    </div>
  );
};
